import { useSearchParams } from 'react-router-dom'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { IncidentsView } from './supply-chain/IncidentsView'
import { ProvidersView } from './supply-chain/ProvidersView'

const VALID_TABS = ['incidents', 'providers'] as const
type Tab = (typeof VALID_TABS)[number]

function parseTab(raw: string | null): Tab {
  return (VALID_TABS as readonly string[]).includes(raw ?? '')
    ? (raw as Tab)
    : 'incidents'
}

/**
 * `/threats/supply-chain` — tabbed shell for the supply-chain surface.
 *
 * Owns `?tab=` and renders shadcn Tabs with two panels:
 * - Incidents (default): the original Surface 1 — stats rail, filter
 *   bar, paginated incident feed.
 * - Providers: the tracked-provider grid previously at /providers
 *   (PR #582). Provider detail at /providers/:slug stays unchanged.
 *
 * URL strategy mirrors AnalyticsPageShell exactly (search param,
 * `replace: true` on tab change). Old /providers list URL redirects
 * here in App.tsx.
 */
export function ThreatsSupplyChainPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = parseTab(searchParams.get('tab'))

  const setTab = (t: Tab) => {
    const next = new URLSearchParams(searchParams)
    next.set('tab', t)
    setSearchParams(next, { replace: true })
  }

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 p-4 md:p-6">
      <header>
        <h1 className="text-2xl font-semibold">Supply Chain &amp; Cloud Security</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Hyperscaler and identity-provider incidents — status pages, security
          bulletins, and CVE cross-refs.
        </p>
      </header>

      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
        <TabsList>
          <TabsTrigger value="incidents">Incidents</TabsTrigger>
          <TabsTrigger value="providers">Providers</TabsTrigger>
        </TabsList>
        <TabsContent value="incidents" className="mt-4">
          <IncidentsView />
        </TabsContent>
        <TabsContent value="providers" className="mt-4">
          <ProvidersView />
        </TabsContent>
      </Tabs>
    </main>
  )
}

export default ThreatsSupplyChainPage
